import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { WebLink } from '@news-mono/web-common'
import {
    breakpoint,
    BreakpointKeys,
    calcRem,
    colors,
    fonts,
    themedValue,
    transition,
} from '../..'
import {
    StyledSubscribeWithGoogleButton,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackageButton,
} from '../../user-registration/breach/components/SubscriptionPackage/SubscriptionPackage.styled'
import { SwgCampaignOffer } from '../campaigns'
import {
    BottomTextWrap,
    InfoText,
    InfoTextLink,
} from '../../user-registration/breach/components/PuzzlesBreach/PuzzlesBreachwall.styled'

const deviceCompNegativeMargin = '-160px'

const checkmark = require('./assets/check.svg')
const plusmark = require('./assets/plus.svg')

const customBreakpoints = {
    tablet: 'sm',
    desktop: 'md',
}
export const StyledPromoPageContainer = styled('div')(({ theme }) => ({
    fontFamily: theme.fonts.sansSerif,
    fontSize: calcRem(18),
}))

export interface StyledSwgCampaignProps {
    modifier?: SwgCampaignOffer
}

export const StyledIntroCopyArea = styled('div')<StyledSwgCampaignProps>(
    ({ theme, modifier }) => [
        {
            background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 100%), ${
                modifier == 'university-offer'
                    ? tokens.thewest.colors.palette.universityOffer
                          .campaignLilac
                    : theme.sectionValues.primaryColor
            }`,
            backgroundBlendMode: 'overlay, normal',
            color: tokens.thewest.colors.palette.white,
            padding: theme.margins.lg,
            paddingBottom: 0,
            overflow: 'hidden',
            marginBottom: '-12%',

            '@media (max-width: 374px)': {
                marginBottom: 0,
            },

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                margin: '0',
            },

            // 480px / 130px / 640px
            [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                overflow: 'visible',
                position: 'relative',
                gridTemplateColumns: '343px 1fr',
                padding: calcRem(72, 120),
                borderRadius: calcRem(32),
                marginBottom: calcRem(-32),
            },
        },
        modifier === 'footy-pack-2025' && {
            marginBottom: 0,
            paddingBottom: theme.margins.lg,

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                borderRadius: 0,
                marginBottom: 0,
                display: 'flex',
                justifyContent: 'center',
            },
            [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                padding: calcRem(120),
            },
        },
    ],
)

export const StyledIntroCopyAreaInner = styled('div')(({ theme }) => [
    {
        maxWidth: '100%',
        width: '100%',

        display: 'grid',
        gap: `${calcRem(24)} 0%`,
        gridTemplateColumns: '100% 1fr',

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            maxWidth: theme.siteMetrics.mainContentWidth,
            width: theme.siteMetrics.mainContentWidth,

            gap: `${calcRem(24)} 10%`,
            gridTemplateColumns: '480px 1fr',
        },
    },
])

export const StyledIntroTitle = styled('h1')<
    StyledSwgCampaignProps & {
        headerFontSize?: number
    }
>(({ modifier, headerFontSize }) => [
    {
        fontSize: calcRem(46),
        lineHeight: 1,
        letterSpacing: '-0.6px',
        whiteSpace: 'pre-line',

        [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
            gridColumn: '1/3',
            paddingRight: '30vw',
            fontSize: calcRem(headerFontSize || 64),
        },

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            margin: 0,
            paddingRight: 0,
        },
    },
    // Split out the ::after so we don't deal with a ghost after on the footy-pack-2025
    modifier !== 'footy-pack-2025' && {
        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            '&::after': {
                content: `''`,
                display: 'block',
                width: calcRem(48),
                marginTop: calcRem(20),
                borderBottom: `3px solid ${tokens.thewest.colors.palette.white}`,
            },
        },
    },
    // Styling for TheWest SwG Footy 2025 Campaign Offer
    modifier === 'footy-pack-2025' && {
        fontWeight: 700,
        margin: 0,

        [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
            gridColumn: '1 / 2',
            display: 'flex',
            paddingRight: 0,

            fontSize: calcRem(64),
            lineHeight: calcRem(76),
        },
    },
])

export const StyledIntroDescription = styled('p')<StyledSwgCampaignProps>(
    ({ modifier, theme }) => [
        // Split these out so they don't fight us with the footy-pack-2025
        modifier !== 'footy-pack-2025' && {
            '&:first-of-type': {
                marginBottom: '160px',
                whiteSpace: 'pre-line',

                '@media (max-width: 374px)': {
                    marginBottom: theme.margins.lg,
                },

                [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                    marginBottom: theme.margins.lg,
                },
            },
        },
        // Style the footy-pack-2025 campaign offer
        modifier === 'footy-pack-2025' && {
            fontFamily: fonts.thewest.serif,
            fontSize: calcRem(18),
            fontWeight: 400,
            lineHeight: calcRem(28),
            margin: 0,

            gridRow: 2,
            gridColumn: 1,

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                marginBottom: 0,
                gridColumn: '1 / 2',
            },
        },
    ],
)

export const StyledDeviceCompContainer = styled('div')<StyledSwgCampaignProps>(
    ({ theme, modifier }) => [
        {
            width: '100vw',
            textAlign: 'center',
            margin: `0 -${theme.margins.lg}px`,
            marginBottom: deviceCompNegativeMargin,
            position: 'relative',

            '@media (max-width: 374px)': {
                marginBottom: 0,
            },

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                background: 'none',
                margin: 0,
                width: 'auto',
            },

            img: {
                width: calcRem(426),
                transform: `translateY(${deviceCompNegativeMargin}) translateX(20%)`,

                '@media (max-width: 374px)': {
                    display: 'none',
                },

                [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                    transform: 'translateY(-60px)',
                },

                [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                    width: 'auto',
                    position: 'absolute',
                    left: 0,
                    transform: 'translateX(12%)',
                },
            },
        },
        // Style the uni campaign offer
        modifier === 'university-offer' && {
            img: {
                transform: 'translate(0, -160px)',
                width: '100%',

                [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                    transform: 'translateY(-40px)',
                },
                [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                    width: 'auto',
                    minWidth: '600px',
                    transform: 'translateX(12%) translateY(-14px)',
                },
            },
        },
        // Style the footy-2025 campaign offer
        modifier === 'footy-pack-2025' && {
            margin: 0,
            marginBottom: 0,
            gridRow: 3,
            gridColumn: 1,

            width: '100%',
            display: 'flex',
            alignItems: 'center',

            [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                gridRow: '1 / 3',
                gridColumn: '2 / 3',
            },

            img: {
                width: '100%',
                height: 'auto',
                transform: 'translate(0px, 0px)',

                '@media (max-width: 374px)': {
                    display: 'block',
                },

                [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                    transform: 'translate(0px, 0px)',
                },
                [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                    transform: 'translate(0px, 0px)',
                },
            },
        },
    ],
)

export const StyledDetailsCopyArea = styled('div')<StyledSwgCampaignProps>(
    ({ theme, modifier }) => [
        {
            background: tokens.thewest.colors.palette.greyErmine,
            padding: calcRem(64, 32, 32),
            borderRadius: calcRem(0, 0, 32, 32),

            [breakpoint('sm')]: {
                padding: theme.margins.lg,
            },

            [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                padding: calcRem(64, 120),
                display: 'grid',
                gridGap: calcRem(170),
                gridTemplateColumns: 'repeat(2, 1fr)',
                alignItems: 'center',
            },
        },
        // Styling for the footy-pack-2025
        modifier === 'footy-pack-2025' && {
            background: '#FFF',

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                padding: calcRem(64),
                marginLeft: 0,
            },
            [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                display: 'flex',
                justifyContent: 'center',

                marginLeft: calcRem(-29),
            },
        },
    ],
)

export const StyledDetailsCopyAreaInner = styled('div')(({ theme }) => [
    {
        maxWidth: '100%',
        width: '100%',

        display: 'grid',
        gridGap: calcRem(32, 0),
        gridTemplateColumns: '100% 1fr',

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            maxWidth: theme.siteMetrics.mainContentWidth,
            width: theme.siteMetrics.mainContentWidth,

            gridTemplateColumns: '640px 1fr',
            gridGap: calcRem(170),
        },
    },
])

export const StyledCallToActionArea = styled('div')<
    StyledSwgCampaignProps & { hideSwgBtn?: boolean }
>(({ theme, hideSwgBtn, modifier }) => [
    {
        h2: {
            margin: 0,
            fontSize: calcRem(40),
        },

        h3: {
            fontSize: calcRem(24),
        },

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            width: calcRem(320),
        },

        [`${StyledSubscribeWithGoogleButton}, ${StyledSubscriptionPackageButton}`]:
            {
                width: '100%',
                height: '54px',
                '&::after': {
                    maxWidth: '228px',
                },
                [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
                    width: calcRem(300),
                },
                display: hideSwgBtn ? 'none' : undefined,
            },

        [`${StyledSubscriptionPackageButton}`]: {
            marginTop: 0,
        },
    },
    // Styling for the footy-pack-2025
    modifier === 'footy-pack-2025' && {
        height: 'fit-content',

        h2: {
            lineHeight: '120%',
            fontSize: calcRem(38),
            fontWeight: 700,
            fontFamily: fonts.thewest.sansSerif,

            marginBottom: calcRem(16),

            [breakpoint(customBreakpoints.tablet as BreakpointKeys)]: {
                fontSize: calcRem(48),
            },
        },

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            width: '100%',
            padding: calcRem(64, 48),

            borderRadius: calcRem(16),
            border: `${calcRem(1)} solid #E5E5E5`,
        },

        [`${StyledSubscribeWithGoogleButton}, ${StyledSubscriptionPackageButton}`]:
            {
                margin: calcRem(32, 0, 0, 0),
            },
        [`${StyledSubscriptionCancellationMessage}`]: {
            marginBottom: calcRem(32),
        },

        [`${BottomTextWrap}`]: {
            marginTop: 0,
        },
        [`${InfoText}, ${InfoTextLink}`]: {
            margin: 0,

            color: '#2B2D2E',

            fontFamily: fonts.thewest.sansSerif,
            fontSize: calcRem(18),
            fontWeight: 400,
            lineHeight: calcRem(28),
        },
        [`${InfoTextLink}`]: {
            color: theme.sectionValues.primaryColor,
        },
    },
])
export const StyledBenefitsListWrappingComponent = styled(
    'div',
)<StyledSwgCampaignProps>(({ modifier }) => [
    modifier === 'footy-pack-2025' && {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',

        gridColumn: 1,
        gridRow: 2,

        [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
            gridColumn: 2,
            gridRow: 1,

            display: 'flex',

            margin: 'auto 0',
        },
    },
])

export const StyledOriginalSubscriptionPrice = styled(
    'p',
)<StyledSwgCampaignProps>(({ theme, modifier }) => [
    {
        margin: 0,
        fontSize: calcRem(18),
        fontWeight: 700,
        textDecoration: 'line-through',
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),
    },
    // Styling for the footy-pack-2025
    modifier === 'footy-pack-2025' && {
        color: '#535556',

        fontFamily: fonts.thewest.serif,
        fontSize: calcRem(20),
        fontWeight: 500,
        lineHeight: calcRem(28),

        marginBottom: calcRem(8),
    },
])

export const StyledSubscriptionPackagePrice = styled(
    'p',
)<StyledSwgCampaignProps>(({ theme, modifier }) => [
    {
        color: theme.sectionValues.primaryColor,
        fontSize: calcRem(40),
        margin: 0,
        marginBottom: calcRem(32),
        lineHeight: 1,
    },
    // Styling for the university-offer
    modifier === 'university-offer' && {
        color: tokens.thewest.colors.palette.universityOffer.campaignLilac,
    },
    // Styling for the footy-pack-2025
    modifier === 'footy-pack-2025' && {
        fontFamily: fonts.thewest.serif,
        fontSize: calcRem(32),
        fontWeight: 500,

        width: 'fit-content',
        marginBottom: 0,
    },
])

export const StyledSubscriptionPackagePriceContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    gap: calcRem(8),
    marginBottom: calcRem(16),
})
export const StyledSubscriptionTerms = styled('p')<{
    terms: 'price' | 'billing'
}>(({ terms }) => [
    {
        margin: 0,
        marginTop: 'auto',
        color: '#535556',

        fontSize: calcRem(14),
        fontFamily: fonts.thewest.sansSerif,
        fontWeight: 400,
        lineHeight: calcRem(16),
    },
    // Price Terms extra Styling
    terms === 'price' && {
        fontWeight: 500,
        fontSize: calcRem(16),
        lineHeight: calcRem(18),
    },
])

export const StyledBenefitsList = styled('ul')({
    paddingLeft: '1rem',

    li: {
        paddingLeft: calcRem(10),
    },

    'li::marker': {
        content: `url(${checkmark})`,
        width: 12,
        height: 12,
    },

    // 'li:last-of-type::marker': {
    //     content: `url(${plusmark})`,
    // },
})

export const StyledOrderedList = styled('ol')({
    paddingLeft: '1rem',

    li: {
        paddingLeft: calcRem(10),
    },
})

export const StyledWestRewardsLink = styled(WebLink)({
    color: tokens.thewest.colors.palette.black,

    '&:hover': {
        textDecoration: 'none',
        color: tokens.thewest.colors.actions.link.default,
    },

    '&:focus': {
        background:
            tokens.thewest.colors.actions.button.primary.background.default,
        color: tokens.thewest.colors.palette.white,
    },
})

// @ref {libs/component-library/src/user-registration/breach/components/BreachWallContent/BreachWallContent.styled.tsx}
const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    border: `1px solid ${colors.white}`,
    padding: calcRem(theme.margins.sm, theme.margins.xl),
    marginTop: calcRem(theme.margins.lg),
    background: 'none',
    color: colors.white,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    fontSize: calcRem(13),
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))
export const StyledViewAllPackagesButton = styled(StyledButton)(
    ({ theme }) => ({
        display: 'inline',
        color: tokens.thewest.colors.text.primary,
        borderColor: tokens.thewest.colors.text.primary,
        marginBottom: theme.margins.lg,

        '&:hover': {
            background:
                tokens.thewest.colors.actions.button.primary.background.default,
            color: tokens.thewest.colors.actions.button.primary.text.default,
            borderColor:
                tokens.thewest.colors.actions.button.primary.background.default,
        },

        '&:focus': {
            color: tokens.thewest.colors.actions.button.primary.text.default,
        },
    }),
)

export const StyledPromoPageFooter = styled('div')(({ theme }) => ({
    paddingLeft: theme.margins.lg,
    paddingRight: theme.margins.lg,
    textAlign: 'center',
}))

export const StyledBannerImage = styled('img')({
    width: '100%',
    [breakpoint(customBreakpoints.desktop as BreakpointKeys)]: {
        borderRadius: calcRem(32),
    },
})

export const StyledTAndC = styled('div')(({ theme }) => ({
    marginTop: theme.margins.md,
    fontSize: calcRem(14),
    padding: calcRem(0, 16),
}))

export const StyledVerifyButton = styled(StyledButton)<{
    isValid?: boolean
    isSidValid?: boolean
    isEmailValid: boolean
}>(({ theme, isValid, isSidValid, isEmailValid }) => ({
    // display: 'inline',
    background: tokens.thewest.colors.palette.universityOffer.campaignLilac,
    color: tokens.thewest.colors.actions.button.primary.text.default,
    borderColor: tokens.thewest.colors.palette.universityOffer.campaignLilac,
    marginBottom: theme.margins.lg,
    marginTop: 0,
    fontSize: calcRem(14),
    padding: calcRem(14, 26),
    display:
        isValid === undefined || !isValid || !isSidValid || !isEmailValid
            ? 'none'
            : 'block',

    '&:hover': {
        background: tokens.thewest.colors.palette.universityOffer.campaignLilac,
        color: tokens.thewest.colors.actions.button.primary.text.default,
        borderColor:
            tokens.thewest.colors.palette.universityOffer.campaignLilac,
        textDecoration: 'underline',
        opacity: 0.9,
    },

    '&:focus': {
        color: tokens.thewest.colors.actions.button.primary.text.default,
    },

    '&[disabled]': {
        textDecoration: 'none',
        background:
            tokens.thewest.colors.actions.button.primary.background.disabled,
        borderColor:
            tokens.thewest.colors.actions.button.primary.background.disabled,
    },
}))

export const StyledTeacherNumberInput = styled('div')<{ isValid?: boolean }>(
    ({ isValid }) => ({
        padding: 0,
        marginBottom: calcRem(20),
        p: {
            fontSize: calcRem(18),
            fontWeight: 800,
        },
        input: {
            textIndent: calcRem(10),
            backgroundColor: '#fff',
            height: calcRem(40),
            outline: 'none',
            width: '100%',
            border: `1px solid ${
                isValid === undefined || isValid
                    ? colors.black
                    : colors.thewest.engineRed
            }`,
        },
        small: {
            color: colors.thewest.engineRed,
            display: isValid === undefined || isValid ? 'none' : 'block',
        },
    }),
)

// Styling for the footy SWG Campaign Offer
export const StyledFootyBenefitList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: calcRem(20),
})
export const StyledFootyBenefitListItem = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    gap: calcRem(12),

    svg: {
        marginTop: calcRem(8),
        height: calcRem(20),
        width: calcRem(20),
    },
})
export const StyledFootyBenefitListText = styled('p')({
    margin: 0,

    color: '#2B2D2E',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontFamily: fonts.thewest.serif,
    fontSize: calcRem(16),
    fontWeight: 400,
    lineHeight: 1.5,
})
